import { useState, useEffect } from "react"
import * as React from "react"
import { getShortMonthsOptions, isAfter, isSameOrBefore } from "@oysterhr/common"
import { pad } from "../utils/numberUtils"
import { useTranslation } from "react-i18next"

import classNames from "classnames"
import { DateTime } from "luxon"
import { Tooltip, Icon, ButtonGroup, Button } from "@oysterhr/ui"

const JANUARY = 1
const DECEMBER = 12

export type MonthValue =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12"
export type Period = `${number}${number}${number}${number}-${MonthValue}`

export enum DisallowedMonthReason {
  Taken = "taken",
  Closed = "closed",
  StartDateNotReached = "startDateNotReached",
}

interface DisallowedMonth {
  reason: DisallowedMonthReason
  tooltip?: string
  error?: string
}

export interface DisallowedMonths {
  [month: number]: DisallowedMonth
}

interface CommonProps {
  yearNavOnly?: false
  monthValue: MonthValue
  onMonthChange: (month: MonthValue) => void
  yearValue?: string
  onYearChange: (year: number) => void
  /**
   * Locked periods. lockBeforePeriod is inclusive.
   */
  lockPeriodBefore?: Period
  /**
   * Locked periods. lockAfterPeriod is exclusive.
   */
  lockPeriodAfter?: Period
  disallowedMonths?: DisallowedMonths
  withReset?: true
  fullWidth?: true
  onReset?: () => void
}

type YearNavOnly = {
  yearNavOnly: true
  yearValue: number
  onYearChange: (year: number) => void
  withHeader?: never
  onMonthAndYearChange?: never
  monthValue?: never
  onMonthChange?: never
  lockPeriodBefore?: never
  lockPeriodAfter?: never
  disallowedMonths?: never
  withReset?: true
  fullWidth?: true
  onReset?: () => void
}
type WithoutCalendarHeader = { withHeader?: false; onMonthAndYearChange?: never }
export type WithCalendarHeader = {
  withHeader: true
  onMonthAndYearChange: (month: MonthValue, year: number) => void
}

type ConditionalProps = WithoutCalendarHeader | WithCalendarHeader

type MonthYearCalendarProps = (CommonProps & ConditionalProps) | YearNavOnly

type MonthObject = { label: string; value: MonthValue }

// const isWithHeader = (props: ConditionalProps): props is WithCalendarHeader => {
//   // check if the specified property is in the given object
//   return "onMonthAndYearChange" in props
// }

const MonthYearCalendar = (props: MonthYearCalendarProps) => {
  const {
    monthValue,
    onMonthChange,
    yearValue,
    onYearChange,
    onMonthAndYearChange,
    lockPeriodBefore,
    lockPeriodAfter,
    withHeader = false,
    yearNavOnly = false,
    disallowedMonths,
    onReset,
    withReset = false,
    fullWidth = false,
  } = props

  const { t } = useTranslation("Shared")
  const months = getShortMonthsOptions(t) as MonthObject[]

  const [open, setOpen] = useState(withHeader || yearNavOnly ? false : true)

  const lockPeriod = lockPeriodBefore?.split("-")
  const lockPeriodAfterDate = lockPeriodAfter?.split("-")

  const lockYear = lockPeriod?.[0]

  const lockYearAfter = lockPeriodAfterDate?.[0]

  const year = Number(yearValue) || new Date().getFullYear()

  const canGoToNextYear = () => {
    if (lockYear && lockYearAfter) {
      return year >= Number(lockYear) && year < Number(lockYearAfter)
    } else if (lockYear && !lockYearAfter) {
      return year >= Number(lockYear)
    } else if (!lockYear && lockYearAfter) {
      return year < Number(lockYearAfter)
    } else {
      return true
    }
  }

  const handleMonthSelect = (month: MonthValue) => {
    onMonthChange?.(month)
    setOpen(false)
  }
  const goToPreviousYear = () => onYearChange(year - 1)
  const goToNextYear = () => {
    if (canGoToNextYear()) {
      onYearChange(year + 1)
    }
  }

  const isSelectionDisabled = (m?: MonthValue, y?: number) => {
    const date = DateTime.fromObject({ month: Number(m), year: y })
    return (
      (lockPeriodBefore && isSameOrBefore(date, lockPeriodBefore)) ||
      (lockPeriodAfter && isAfter(date, lockPeriodAfter))
    )
  }

  const YearNavigation = () => {
    const isDisabled = year + 1 > new Date().getFullYear()
    return (
      <div className="flex min-h-11 items-center justify-between gap-4 rounded-lg border border-gray-300">
        <Button
          variation="borderless"
          size="sm"
          onClick={goToPreviousYear}
          className="cursor-pointer border-r border-gray-200 px-2.5 py-1.5"
          data-testid="calendar-previous-year"
        >
          <Icon name="ArrowLineLeft" />
        </Button>
        <Button variation="borderless" size="sm">
          {year}
        </Button>
        <Button
          variation="borderless"
          size="sm"
          onClick={goToNextYear}
          disabled={isDisabled}
          className={classNames("cursor-pointer border-l border-gray-200 px-2.5 py-1.5", {
            "cursor-not-allowed text-gray-200": isDisabled,
            "text-gray-400": !isDisabled,
          })}
          data-testid="calendar-next-year"
        >
          <Icon name="ArrowRight" />
        </Button>
      </div>
    )
  }

  const CalendarHeader = () => {
    const monthSelected = months.find(month => month.value === monthValue) || undefined

    const getCurrentMonth = () => {
      return months.find(month => Number(month.value) === new Date().getMonth() + 1)
    }

    const getPreviousMonth = (value = monthSelected?.value || getCurrentMonth()?.value) => {
      return Number(value) === JANUARY ? pad(DECEMBER) : pad(Number(value) - 1)
    }

    const getNextMonth = (value = monthSelected?.value || getCurrentMonth()?.value) => {
      return Number(value) === DECEMBER ? pad(JANUARY) : pad(Number(value) + 1)
    }

    const goToNextMonth = () => {
      const nextMonth = getNextMonth() as MonthValue
      if (Number(monthSelected?.value) === DECEMBER) {
        onMonthAndYearChange?.(nextMonth, year + 1)
      } else {
        onMonthChange?.(nextMonth)
      }
    }

    const goToPreviousMonth = () => {
      const previousMonth = getPreviousMonth() as MonthValue
      if (Number(monthSelected?.value) === JANUARY) {
        onMonthAndYearChange?.(previousMonth, year - 1)
      } else {
        onMonthChange?.(previousMonth)
      }
    }

    const previousMonthDisabled = isSelectionDisabled(
      getPreviousMonth() as MonthValue,
      Number(getPreviousMonth()) === DECEMBER ? year - 1 : year,
    )
    const nextMonthDisabled = isSelectionDisabled(
      getNextMonth() as MonthValue,
      Number(getNextMonth()) === JANUARY ? year + 1 : year,
    )

    useEffect(() => {
      if (!yearValue) {
        onYearChange(new Date().getFullYear())
      }
    }, [monthSelected])

    return (
      <ButtonGroup className={classNames({ "[&]:flex": fullWidth })}>
        <Button
          variation="borderless"
          onClick={goToPreviousMonth}
          disabled={previousMonthDisabled}
          className={classNames("grid items-center border-r-2 border-gray-300 px-2.5", {
            "cursor-not-allowed text-gray-200": previousMonthDisabled,
            "cursor-pointer text-gray-400": !previousMonthDisabled,
          })}
          data-testid="calendar-previous-month"
        >
          <Icon name="ArrowLeft" />
        </Button>
        <Button
          onClick={() => setOpen(!open)}
          className={classNames("flex items-center", {
            "grow justify-center": fullWidth,
          })}
          data-testid="calendar-select-button"
        >
          <span>
            {yearValue && monthSelected ? `${monthSelected.label} ${year}` : "Select a date"}
          </span>
          <Icon name="CalendarBlank" className="ml-2.5" />
        </Button>
        <Button
          variation="borderless"
          onClick={goToNextMonth}
          disabled={nextMonthDisabled}
          className={classNames("grid items-center border-l-2 border-gray-300 px-2.5 font-bold", {
            "cursor-not-allowed text-gray-200": nextMonthDisabled,
            "cursor-pointer text-gray-400": !nextMonthDisabled,
          })}
          data-testid="calendar-next-month"
        >
          <Icon name="ArrowRight" />
        </Button>
      </ButtonGroup>
    )
  }

  return (
    <div className="relative">
      {yearNavOnly && <YearNavigation />}
      {withHeader && <CalendarHeader />}
      {open && (
        <div
          className="absolute z-50 w-full rounded bg-white p-4"
          data-testid="month-select"
          style={{
            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div className="mb-4 flex items-center justify-between">
            <Button
              type="button"
              variation="borderless"
              size="sm"
              className={classNames({
                "cursor-not-allowed text-gray-200":
                  year - 1 < Number(lockYear) || year - 1 > Number(lockYearAfter),
                "cursor-pointer text-gray-300":
                  year - 1 >= Number(lockYear) || year - 1 <= Number(lockYearAfter),
              })}
              disabled={year - 1 < Number(lockYear) || year - 1 > Number(lockYearAfter)}
              onClick={goToPreviousYear}
              data-testid="calendar-inner-previous-year"
            >
              <Icon name="ChevronLeft" />
            </Button>
            <div className="text-base	font-semibold">{yearValue}</div>
            <Button
              type="button"
              variation="borderless"
              size="sm"
              className={classNames({
                "cursor-not-allowed text-gray-200": !canGoToNextYear(),
                "cursor-pointer text-gray-300": canGoToNextYear(),
              })}
              disabled={!canGoToNextYear()}
              onClick={goToNextYear}
              data-testid="calendar-inner-next-year"
            >
              <Icon name="ChevronRight" />
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {months.map(month => {
              const isMonthSelected = month.value === monthValue
              const selectionDisabled = isSelectionDisabled(month.value, year)
              const disallowedMonth = disallowedMonths?.[Number(month.value)]

              return disallowedMonth ? (
                <div
                  key={month.label}
                  className={classNames(
                    "flex items-center justify-center rounded text-base text-gray-200",
                    {
                      "bg-info-lighten-100": disallowedMonth.reason === DisallowedMonthReason.Taken,
                      "bg-gray-100": disallowedMonth.reason === DisallowedMonthReason.Closed,
                    },
                  )}
                >
                  <Tooltip key={month.label} value={disallowedMonth.tooltip}>
                    <Button variation="borderless" size="sm" aria-disabled>
                      {month.label}
                    </Button>
                  </Tooltip>
                </div>
              ) : (
                <Button
                  variation={isMonthSelected ? "outline" : "borderless"}
                  size="sm"
                  disabled={selectionDisabled}
                  onClick={() => handleMonthSelect(month.value)}
                  key={month.label}
                >
                  {month.label}
                </Button>
              )
            })}
          </div>
          {withReset && (
            <div className="flex justify-center">
              <Button
                variation="secondary"
                isFullWidth
                size="sm"
                className="m-4 cursor-pointer"
                onClick={() => {
                  setOpen(false)
                  if (onReset) {
                    onReset()
                  }
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MonthYearCalendar
